// frontend/src/pages/dashboard/ProfilePage.js
import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  Box, Typography, TextField, Button, Paper, Alert,
  FormControl, InputLabel, OutlinedInput, InputAdornment,
  IconButton, Avatar, Chip, Grid, Card, CardContent,
  Divider, Switch, FormControlLabel, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, Tabs, Tab, Backdrop,
  LinearProgress, Tooltip, ToggleButtonGroup, ToggleButton, MenuItem, Select,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer
} from '@mui/material';
import {
  Visibility, VisibilityOff, Save as SaveIcon, Work as WorkIcon,
  Business as BusinessIcon, LinkedIn as LinkedInIcon,
  GitHub as GitHubIcon, Psychology as PsychologyIcon,
  Close as CloseIcon, Person as PersonIcon, Email as EmailIcon,
  Warning as WarningIcon, TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon, Assessment as AssessmentIcon,
  AccessTime as TimezoneIcon
} from '@mui/icons-material';
import api, { userAPI } from '../../services/api';

const ProfilePage = () => {
  const { user, updateUser, logout } = useAuth();
  const allTimezones = Intl.supportedValuesOf('timeZone');
  const isAdmin = user?.teamMembership?.role?.name?.toLowerCase() === 'system administrator';
  const isClient = user?.client_id !== null && user?.client_id !== undefined;

  // Form Data
  const [formData, setFormData] = useState({
    name: '', email: '', current_password: '', new_password: '',
    new_password_confirmation: '', job_title: '', bio: '',
    department: '', linkedin_url: '', github_url: '',
    skills: [], allow_ai_analysis: true, joining_date: '', terminated_at: '',
  });

  // Password Visibility
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // UI States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // KPI View States (READ-ONLY)
  const [kpiPeriod, setKpiPeriod] = useState('monthly'); // monthly | yearly
  const [kpiData, setKpiData] = useState([]);
  const [kpiLoading, setKpiLoading] = useState(false);

  // Skill Dialog
  const [skillDialogOpen, setSkillDialogOpen] = useState(false);
  const [currentSkill, setCurrentSkill] = useState({ name: '', level: 'beginner', years: '' });
  const [removeSkillDialogOpen, setRemoveSkillDialogOpen] = useState(false);
  const [skillToRemoveIndex, setSkillToRemoveIndex] = useState(null);

  const [companyData, setCompanyData] = useState({ company_name: '', address: '', phone: '', timezone: 'UTC' });
  const [companyLoading, setCompanyLoading] = useState(false);

  useEffect(() => {
    if (isAdmin && user?.company) {
      setCompanyData({
        company_name: user.company.company_name || '',
        address: user.company.address || '',
        phone: user.company.phone || '',
        timezone: user.company.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      });
    }
  }, [isAdmin, user]);

  const handleCompanyUpdate = async (e) => {
    e.preventDefault();
    setCompanyLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/company-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(companyData),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Company info updated!');
        // Update context if needed
      } else {
        toast.error(data.error || 'Failed to update');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setCompanyLoading(false);
    }
  };

  // Load User Data
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '', email: user.email || '',
        current_password: '', new_password: '', new_password_confirmation: '',
        job_title: user.job_title || '', bio: user.bio || '',
        department: user.department || '', linkedin_url: user.linkedin_url || '',
        github_url: user.github_url || '', skills: user.skills || [],
        allow_ai_analysis: user.allow_ai_analysis ?? true,
        joining_date: user.joining_date ? user.joining_date.split('T')[0] : '',
        terminated_at: user.terminated_at ? user.terminated_at.split('T')[0] : '',
      });
    }
    if (!isClient) {
      fetchPerformanceData();
      fetchKpiData();
    }
  }, [user, isClient, kpiPeriod]);

  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return null;
    const baseUrl = process.env.REACT_APP_AVATAR_URL?.replace(/\/+$/, '') || 'http://localhost:8000/storage';
    const cleanPath = avatarPath.replace(/^\/+/, '');
    return `${baseUrl}/${cleanPath}?t=${Date.now()}`;
  };

  // Fetch Auto Performance Data
  const fetchPerformanceData = async () => {
    try {
      const res = await userAPI.getPerformance();
      setPerformanceData(res.data);
    } catch (err) {
      console.error('Failed to load performance data:', err);
    }
  };

  // ✅ Fetch AUTO KPIs (Monthly/Yearly) - READ ONLY
  const fetchKpiData = async () => {
    if (isClient) return;
    setKpiLoading(true);
    try {
      const res = await userAPI.getKpiReport({ period_type: kpiPeriod });
      setKpiData(res.data.data || []);
    } catch (err) {
      console.error('Failed to load KPI data:', err);
      setKpiData([]);
    } finally {
      setKpiLoading(false);
    }
  };

  // Form Change Handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  // Avatar Upload
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  // ==================== SKILL MANAGEMENT ====================
  const handleAddSkill = async () => {
    if (!currentSkill.name.trim()) return;
    const newSkill = { ...currentSkill, id: Date.now() };
    setFormData(prev => ({ ...prev, skills: [...prev.skills, newSkill] }));
    try {
      const token = localStorage.getItem('access_token');
      await fetch(`${process.env.REACT_APP_API_URL}/users/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ skills: [...formData.skills, newSkill] }),
      });
      toast.success('✅ Skill added successfully!');
    } catch (err) {
      setFormData(prev => ({ ...prev, skills: prev.skills.filter(s => s.id !== newSkill.id) }));
      toast.error('❌ Failed to save skill');
    }
    setCurrentSkill({ name: '', level: 'beginner', years: '' });
    setSkillDialogOpen(false);
  };

  const handleRemoveSkillClick = (index) => {
    setSkillToRemoveIndex(index);
    setRemoveSkillDialogOpen(true);
  };

  const handleRemoveSkillConfirm = async () => {
    if (skillToRemoveIndex === null) return;
    const skillName = formData.skills[skillToRemoveIndex]?.name || 'This skill';
    const oldSkills = [...formData.skills];
    setFormData(prev => ({ ...prev, skills: prev.skills.filter((_, i) => i !== skillToRemoveIndex) }));
    try {
      const token = localStorage.getItem('access_token');
      await fetch(`${process.env.REACT_APP_API_URL}/users/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ skills: formData.skills.filter((_, i) => i !== skillToRemoveIndex) }),
      });
      toast.success(`✅ ${skillName} removed successfully!`);
    } catch (err) {
      setFormData(prev => ({ ...prev, skills: oldSkills }));
      toast.error('❌ Failed to remove skill');
    }
    setSkillToRemoveIndex(null);
    setRemoveSkillDialogOpen(false);
  };

  // ==================== PROFILE SUBMIT ====================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    const payload = {};
    let hasChanges = false;

    if (formData.name !== user.name) { payload.name = formData.name; hasChanges = true; }
    if (formData.email !== user.email) { payload.email = formData.email; hasChanges = true; }

    if (formData.new_password) {
      if (formData.new_password !== formData.new_password_confirmation) {
        setError('New passwords do not match.');
        setLoading(false);
        return;
      }
      if (!formData.current_password) {
        setError('Current password is required to change password.');
        setLoading(false);
        return;
      }
      payload.current_password = formData.current_password;
      payload.new_password = formData.new_password;
      payload.new_password_confirmation = formData.new_password_confirmation;
      hasChanges = true;
    }

    if (!isClient) {
      if (formData.job_title !== user.job_title) { payload.job_title = formData.job_title; hasChanges = true; }
      if (formData.bio !== user.bio) { payload.bio = formData.bio; hasChanges = true; }
      if (formData.department !== user.department) { payload.department = formData.department; hasChanges = true; }
      if (formData.linkedin_url !== user.linkedin_url) { payload.linkedin_url = formData.linkedin_url; hasChanges = true; }
      if (formData.github_url !== user.github_url) { payload.github_url = formData.github_url; hasChanges = true; }
      if (formData.allow_ai_analysis !== (user.allow_ai_analysis ?? true)) { 
        payload.allow_ai_analysis = formData.allow_ai_analysis; 
        hasChanges = true; 
      }
      
      // joining_date
      if (formData.joining_date && formData.joining_date !== user.joining_date) {
        payload.joining_date = formData.joining_date;
        hasChanges = true;
      }
      
      // terminated_at - only send if has actual value
      if (formData.terminated_at && formData.terminated_at.trim() !== '' && formData.terminated_at !== user.terminated_at) {
        payload.terminated_at = formData.terminated_at;
        hasChanges = true;
      } else if (!formData.terminated_at && user.terminated_at) {
        // If clearing the field, send null explicitly
        payload.terminated_at = null;
        hasChanges = true;
      }
      
      payload.skills = formData.skills;
    }

    if (!hasChanges && !avatarFile) {
      setError('No changes detected.');
      setLoading(false);
      return;
    }

    try {
      let res;
      
      if (avatarFile) {
        const formDataToSend = new FormData();
        for (const key in payload) {
          if (key === 'skills') {
            formDataToSend.append(key, JSON.stringify(payload[key]));
          } else if (payload[key] === null) {
            formDataToSend.append(key, '');  // Send empty string for null in FormData
          } else {
            formDataToSend.append(key, payload[key]);
          }
        }
        formDataToSend.append('avatar', avatarFile);
        
        res = await api.post('/users/profile', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: { _method: 'PUT' }
        });
      } else {
        res = await userAPI.updateProfile(payload);
      }

      const data = res.data;
      
      if (payload.new_password) {
        toast.success('🔐 Password updated successfully!');
      } else {
        toast.success('✅ Profile updated successfully!');
      }
      
      if (data.user) {
        updateUser(data.user);
        setPreview(null);
        if (!isClient) {
          fetchPerformanceData();
          fetchKpiData();
        }
      }
      
      if (payload.email) {
        setTimeout(() => { logout(); window.location.href = '/login'; }, 1500);
      }
      
    } catch (err) {
      console.error('Profile update error:', err);
      let errorMsg = 'Network error. Please try again.';
      if (err.response?.data) {
        const data = err.response.data;
        if (data.errors) {
          errorMsg = Object.values(data.errors)[0]?.[0] || errorMsg;
        } else if (data.message) {
          errorMsg = data.message;
        } else if (data.error) {
          errorMsg = data.error;
        }
      }
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };


  const getLevelColor = (level) => {
    switch (level) {
      case 'expert': return 'success';
      case 'advanced': return 'info';
      case 'intermediate': return 'warning';
      default: return 'default';
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2, md: 3 }, background: '#f5f7fa' }}>
        <Typography variant="h4" gutterBottom fontWeight={700} color="text.primary" sx={{ mb: 3 }}>
          Profile Settings
        </Typography>

        {/* ✅ Performance Overview Card - Client হলে দেখাবে না */}
        {!isClient && performanceData && (
          <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>Performance Overview</Typography>
                  {/* <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Last updated: {performanceData.user.last_performance_review_at
                      ? new Date(performanceData.user.last_performance_review_at).toLocaleDateString()
                      : 'Never'}
                  </Typography> */}
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" fontWeight={700}>{performanceData.performance?.score || 0}</Typography>
                  <Typography variant="body2">Performance Score</Typography>
                </Box>
                <Grid container spacing={2} sx={{ mt: 1 }} minWidth={{ xs: '100%', sm: '40%' }}>
                  <Grid item size={{ xs: 6, sm: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" fontWeight={600}>{performanceData.performance?.tasks_completed || 0}</Typography>
                      <Typography variant="caption">Tasks Done</Typography>
                    </Box>
                  </Grid>
                  <Grid item size={{ xs: 6, sm: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" fontWeight={600}>{performanceData.performance?.on_time_rate || 0}%</Typography>
                      <Typography variant="caption">On Time</Typography>
                    </Box>
                  </Grid>
                  <Grid item size={{ xs: 6, sm: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" fontWeight={600}>{performanceData.performance?.total_hours || 0}h</Typography>
                      <Typography variant="caption">Hours Tracked</Typography>
                    </Box>
                  </Grid>
                  <Grid item size={{ xs: 6, sm: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" fontWeight={600}>{performanceData.skills?.length || 0}</Typography>
                      <Typography variant="caption">Skills</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48, px: { xs: 1, sm: 2 } }
          }}>
            <Tab label="Basic Info" />
            <Tab label="Skills" />
            {!isClient && <Tab label="My KPIs (Auto)" />}
            <Tab label="Password" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Grid container spacing={3}>


            {isAdmin && (
              <Grid item size={{ xs: 12 }}>
                <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', mt: 3, border: '1px solid #4361ee' }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom color="primary">
                    <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Company Information
                  </Typography>
                  <form onSubmit={handleCompanyUpdate}>
                    <Grid container spacing={2}>
                      <Grid item size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth label="Company Name" value={companyData.company_name} onChange={(e) => setCompanyData({ ...companyData, company_name: e.target.value })} margin="normal" />
                      </Grid>
                      <Grid item size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth label="Phone" value={companyData.phone} onChange={(e) => setCompanyData({ ...companyData, phone: e.target.value })} margin="normal" />
                      </Grid>
                      <Grid item size={{ xs: 12 }}>
                        <TextField fullWidth label="Address" multiline rows={2} value={companyData.address} onChange={(e) => setCompanyData({ ...companyData, address: e.target.value })} margin="normal" />
                      </Grid>
                      {/* Timezone Dropdown */}
                      <Grid item size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          select
                          label="Timezone"
                          name="timezone"
                          value={companyData.timezone}
                          onChange={(e) => setCompanyData({ ...companyData, timezone: e.target.value })}
                          margin="normal"
                          SelectProps={{
                            MenuProps: {
                              PaperProps: {
                                sx: {
                                  maxHeight: 300,
                                  '& .MuiMenuItem-root': {
                                    fontSize: '0.875rem',
                                    py: 0.5
                                  }
                                }
                              }
                            }
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <TimezoneIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        >
                          {allTimezones.map((tz) => (
                            <MenuItem key={tz} value={tz}>
                              {tz}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item size={{ xs: 12 }}>
                        <Button type="submit" variant="contained" disabled={companyLoading} sx={{ mt: 2 }}>
                          {companyLoading ? 'Updating...' : 'Update Company Info'}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Paper>
              </Grid>
            )}



            <Grid item size={{ xs: 12, sm: 6 }}>
              <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar src={preview || getAvatarUrl(user?.avatar)} sx={{ width: 80, height: 80, border: '2px solid #e0e0e0' }} />
                  <Box>
                    <Button variant="outlined" component="label" size="small" sx={{ borderRadius: 2, textTransform: 'none' }}>
                      Upload Photo
                      <input type="file" hidden accept="image/*" onChange={handleAvatarChange} />
                    </Button>
                  </Box>
                </Box>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                <form onSubmit={handleSubmit}>
                  <TextField fullWidth label="Full Name" name="name" value={formData.name} onChange={handleChange} margin="normal" sx={{ mb: 2 }}
                    InputProps={{ startAdornment: (<InputAdornment position="start"><PersonIcon /></InputAdornment>) }} />
                  <TextField fullWidth label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} margin="normal" sx={{ mb: 2 }}
                    InputProps={{ startAdornment: (<InputAdornment position="start"><EmailIcon /></InputAdornment>) }} />

                  {!isClient && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        <WorkIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2rem' }} /> Professional Information
                      </Typography>
                      <TextField fullWidth label="Job Title" name="job_title" value={formData.job_title} onChange={handleChange} margin="normal" sx={{ mb: 2 }}
                        InputProps={{ startAdornment: (<InputAdornment position="start"><WorkIcon /></InputAdornment>) }} />
                      <TextField
                        fullWidth
                        label="Joining Date"
                        name="joining_date"
                        type="date"
                        value={formData.joining_date}
                        onChange={handleChange}
                        margin="normal"
                        sx={{ mb: 2 }}
                        InputLabelProps={{ shrink: true }}
                      />
                      {isAdmin && (
                        <TextField
                          fullWidth
                          label="Terminated Date (Admin Only)"
                          name="terminated_at"
                          type="date"
                          value={formData.terminated_at}
                          onChange={handleChange}
                          margin="normal"
                          sx={{ mb: 2 }}
                          InputLabelProps={{ shrink: true }}
                          helperText="Set date to mark employment end"
                        />
                      )}
                      <TextField fullWidth label="Department" name="department" value={formData.department} onChange={handleChange} margin="normal" sx={{ mb: 2 }}
                        InputProps={{ startAdornment: (<InputAdornment position="start"><BusinessIcon /></InputAdornment>) }} />
                      <TextField fullWidth label="Bio" name="bio" multiline rows={3} value={formData.bio} onChange={handleChange} margin="normal" sx={{ mb: 2 }}
                        InputProps={{ startAdornment: (<InputAdornment position="start"><PsychologyIcon /></InputAdornment>) }} />
                      <TextField fullWidth label="LinkedIn URL" name="linkedin_url" value={formData.linkedin_url} onChange={handleChange} margin="normal" sx={{ mb: 2 }}
                        InputProps={{ startAdornment: (<InputAdornment position="start"><LinkedInIcon /></InputAdornment>) }} />
                      <TextField fullWidth label="GitHub URL" name="github_url" value={formData.github_url} onChange={handleChange} margin="normal" sx={{ mb: 2 }}
                        InputProps={{ startAdornment: (<InputAdornment position="start"><GitHubIcon /></InputAdornment>) }} />
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        <PsychologyIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2rem' }} /> AI Automation Settings
                      </Typography>
                      <FormControlLabel control={<Switch checked={formData.allow_ai_analysis} onChange={(e) => setFormData(prev => ({ ...prev, allow_ai_analysis: e.target.checked }))} color="primary" />}
                        label="Allow AI analysis of my performance data" />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        When enabled, your performance data can be used for AI-powered insights and automation.
                      </Typography>
                    </>
                  )}
                  <Box sx={{ mt: 3 }}>
                    <Button type="submit" variant="contained" disabled={loading} fullWidth sx={{ py: 1.5, fontWeight: 600, textTransform: 'none', background: 'linear-gradient(135deg, #4361ee, #3a0ca3)', '&:hover': { background: 'linear-gradient(135deg, #3a50e2, #2d0a9a)' } }}>
                      {loading ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                  </Box>
                </form>
              </Paper>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">Profile Summary</Typography>
                <List>
                  <ListItem><ListItemText primary="Email" secondary={user?.email} /></ListItem>
                  <ListItem><ListItemText primary="Role" secondary={user?.teamMembership?.role?.name || 'N/A'} /></ListItem>
                  <ListItem><ListItemText primary="Join Date" secondary={user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'} /></ListItem>
                </List>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">Account Log</Typography>
                <List>
                  <ListItem><ListItemText primary="Last Active" secondary={user?.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : 'Never'} /></ListItem>
                  <ListItem><ListItemText primary="Password Last Changed" secondary={user?.password_updated_at ? new Date(user.password_updated_at).toLocaleDateString() : 'Never'} /></ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* ✅ SKILLS TAB (Manual - User can add/remove) */}
        {activeTab === 1 && (
          <Grid container spacing={3}>
            <Grid item size={{ xs: 12 }}>
              <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600} color="text.primary">
                    <WorkIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2rem' }} /> My Skills
                  </Typography>
                  {!isClient && (
                    <Button size="small" startIcon={<WorkIcon />} onClick={() => setSkillDialogOpen(true)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>
                      Add Skill
                    </Button>
                  )}
                </Box>
                {formData.skills.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No skills added yet</Typography>
                  </Box>
                ) : (
                  <List dense>
                    {formData.skills.map((skill, index) => (
                      <ListItem key={skill.id || index} secondaryAction={
                        !isClient && <IconButton edge="end" onClick={() => handleRemoveSkillClick(index)}><CloseIcon /></IconButton>
                      }>
                        <ListItemText primary={skill.name} secondary={`Level: ${skill.level} ${skill.years ? `• ${skill.years} years` : ''}`} />
                        <Chip label={skill.level} size="small" color={getLevelColor(skill.level)} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* ✅ AUTO KPIs TAB (READ-ONLY - Monthly/Yearly View) */}
        {!isClient && activeTab === 2 && (
          <Grid container spacing={3}>
            <Grid item size={{ xs: 12 }}>
              <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} color="text.primary">
                    <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2rem' }} /> My Performance (Auto-Calculated)
                  </Typography>
                  <ToggleButtonGroup value={kpiPeriod} exclusive onChange={(e, val) => val && setKpiPeriod(val)} size="small">
                    <ToggleButton value="monthly">Monthly</ToggleButton>
                    <ToggleButton value="yearly">Yearly</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {kpiLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                ) : kpiData.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <AssessmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary">No performance data available yet</Typography>
                    <Typography variant="caption" color="text.secondary">KPIs are automatically calculated based on your tasks, projects, and time tracking</Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Tasks Completed</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>On Time %</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Projects</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Sprint Velocity</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Backlog Cleared</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {kpiData.map((row, idx) => {
                          const progressPercent = row.tasks_completed > 0 ? Math.round(((row.tasks_on_time || 0) / row.tasks_completed) * 100) : 0;
                          return (
                            <TableRow key={idx} hover>
                              <TableCell>
                                <Chip label={row.month || row.year} size="small" color="primary" variant="outlined" />
                              </TableCell>
                              <TableCell>{row.tasks_completed || 0}</TableCell>
                              <TableCell>
                                <Chip label={`${row.tasks_on_time_percentage || 0}%`} size="small" color={progressPercent >= 80 ? 'success' : 'warning'} />
                              </TableCell>
                              <TableCell>{row.projects_completed || 0}</TableCell>
                              <TableCell>{row.sprint_velocity || 0}</TableCell>
                              <TableCell>{row.backlog_cleared || 0}</TableCell>
                              <TableCell>
                                <Typography fontWeight="bold" color={row.performance_score >= 80 ? 'success.main' : 'primary.main'}>
                                  {row.performance_score || 0}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                  <Typography variant="body2">
                    ℹ️ These KPIs are automatically calculated based on your completed tasks, projects, sprints, and backlog items.
                    No manual input required.
                  </Typography>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Password Tab */}
        {activeTab === 3 && (
          <Grid container spacing={3}>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">Change Password</Typography>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Current Password</InputLabel>
                  <OutlinedInput type={showCurrentPassword ? 'text' : 'password'} value={formData.current_password} onChange={handleChange} name="current_password"
                    endAdornment={<InputAdornment position="end"><IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)}>{showCurrentPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>} />
                </FormControl>
                <FormControl fullWidth margin="normal">
                  <InputLabel>New Password</InputLabel>
                  <OutlinedInput type={showNewPassword ? 'text' : 'password'} value={formData.new_password} onChange={handleChange} name="new_password"
                    endAdornment={<InputAdornment position="end"><IconButton onClick={() => setShowNewPassword(!showNewPassword)}>{showNewPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>} />
                </FormControl>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Confirm New Password</InputLabel>
                  <OutlinedInput type={showConfirmPassword ? 'text' : 'password'} value={formData.new_password_confirmation} onChange={handleChange} name="new_password_confirmation"
                    endAdornment={<InputAdornment position="end"><IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>{showConfirmPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>} />
                </FormControl>
                <Box sx={{ mt: 3 }}>
                  <Button variant="contained" onClick={handleSubmit} disabled={loading} fullWidth sx={{ py: 1.5, fontWeight: 600, textTransform: 'none', background: 'linear-gradient(135deg, #4361ee, #3a0ca3)', '&:hover': { background: 'linear-gradient(135deg, #3a50e2, #2d0a9a)' } }}>
                    {loading ? <CircularProgress size={24} /> : 'Update Password'}
                  </Button>
                </Box>
              </Paper>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">Security Tips</Typography>
                <List>
                  <ListItem><ListItemText primary="Use a strong password with at least 8 characters" /></ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Skill Dialog - Client হলে Open হবে না */}
      {!isClient && (
        <Dialog open={skillDialogOpen} onClose={() => setSkillDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>Add Skill
            <IconButton aria-label="close" onClick={() => setSkillDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField fullWidth label="Skill Name" value={currentSkill.name} onChange={(e) => setCurrentSkill(prev => ({ ...prev, name: e.target.value }))} margin="normal" />
            <FormControl fullWidth margin="normal">
              <InputLabel>Level</InputLabel>
              <Select value={currentSkill.level} onChange={(e) => setCurrentSkill(prev => ({ ...prev, level: e.target.value }))}>
                <MenuItem value="beginner">Beginner</MenuItem>
                <MenuItem value="intermediate">Intermediate</MenuItem>
                <MenuItem value="advanced">Advanced</MenuItem>
                <MenuItem value="expert">Expert</MenuItem>
              </Select>
            </FormControl>
            <TextField fullWidth label="Years of Experience" type="number" value={currentSkill.years} onChange={(e) => setCurrentSkill(prev => ({ ...prev, years: e.target.value }))} margin="normal" />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSkillDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddSkill} variant="contained">Add</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Remove Skill Confirmation */}
      <Dialog open={removeSkillDialogOpen} onClose={() => setRemoveSkillDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}><WarningIcon /> Remove Skill?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">Are you sure you want to remove <strong>{formData.skills[skillToRemoveIndex]?.name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveSkillDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleRemoveSkillConfirm} variant="contained" color="error">Remove</Button>
        </DialogActions>
      </Dialog>

      {/* Loading Overlay */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </DashboardLayout>
  );
};

export default ProfilePage;